"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { X, ZoomIn, ZoomOut, RotateCcw, Download } from "lucide-react";

interface ImageFullScreenViewerProps {
  imageUrl: string;
  alt: string;
  isOpen: boolean;
  onClose: () => void;
  /** Optional: enable a download button (defaults to on). Set false to hide. */
  allowDownload?: boolean;
  /** Optional: filename suggestion for the downloaded image. */
  downloadFilename?: string;
}

interface Transform {
  x: number;
  y: number;
  scale: number;
}

const MIN_SCALE = 1;
const MAX_SCALE = 8;
const WHEEL_ZOOM_SPEED = 2;

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function normalizeWheelDelta(e: WheelEvent): number {
  let dy = e.deltaY;
  if (e.deltaMode === 1) dy *= 8;
  if (e.deltaMode === 2) dy *= 24;
  return Math.sign(dy) * Math.min(24, Math.abs(dy));
}

export function ImageFullScreenViewer({
  imageUrl,
  alt,
  isOpen,
  onClose,
  allowDownload = true,
  downloadFilename,
}: ImageFullScreenViewerProps) {
  const [transform, setTransform] = useState<Transform>({
    x: 0,
    y: 0,
    scale: 1,
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [transformStart, setTransformStart] = useState({ x: 0, y: 0 });
  const touchStateRef = useRef<{
    distance: number;
    scale: number;
    tx: number;
    ty: number;
  } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    if (isOpen) {
      setTransform({ x: 0, y: 0, scale: 1 });
      setIsDragging(false);
      touchStateRef.current = null;
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isOpen, onClose]);

  // Zoom toward a point relative to the image's visual center.
  // x,y offsets represent pixel displacement from center at scale=1.
  // cursorX/cursorY are relative to the image's current visual center on screen.
  const zoomAtPoint = useCallback(
    (
      cursorX: number,
      cursorY: number,
      newScale: number,
      prev: Transform
    ): Transform => {
      // cursorX/cursorY = offset from image center in screen pixels
      // Convert to position in the "content" coordinate space
      const contentX = (cursorX - prev.x) / prev.scale;
      const contentY = (cursorY - prev.y) / prev.scale;

      // After scaling, that content point maps to a new screen position
      // We want it to stay at cursorX/cursorY, so adjust translate
      const newX = cursorX - contentX * newScale;
      const newY = cursorY - contentY * newScale;

      return { x: newX, y: newY, scale: newScale };
    },
    []
  );

  // Get cursor position relative to image center
  const getCursorRelativeToImageCenter = useCallback(
    (clientX: number, clientY: number) => {
      const img = imgRef.current;
      if (!img) return { x: 0, y: 0 };

      // The image's base rect (before transform) — we need its center
      // The displayed rect includes the transform, so compute base center
      const container = containerRef.current;
      if (!container) return { x: 0, y: 0 };

      const containerRect = container.getBoundingClientRect();
      // Container center = image base center (flexbox centers it)
      const baseCenterX = containerRect.left + containerRect.width / 2;
      const baseCenterY = containerRect.top + containerRect.height / 2;

      return {
        x: clientX - baseCenterX,
        y: clientY - baseCenterY,
      };
    },
    []
  );

  // Native wheel listener
  useEffect(() => {
    const el = containerRef.current;
    if (!el || !isOpen) return;

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();

      const cursor = getCursorRelativeToImageCenter(e.clientX, e.clientY);
      const dy = normalizeWheelDelta(e);

      setTransform((prev) => {
        const factor =
          dy <= 0
            ? 1 - (WHEEL_ZOOM_SPEED * dy) / 100
            : 1 / (1 + (WHEEL_ZOOM_SPEED * dy) / 100);

        const newScale = clamp(prev.scale * factor, MIN_SCALE, MAX_SCALE);

        if (newScale <= MIN_SCALE) {
          return { x: 0, y: 0, scale: MIN_SCALE };
        }

        return zoomAtPoint(cursor.x, cursor.y, newScale, prev);
      });
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [isOpen, zoomAtPoint, getCursorRelativeToImageCenter]);

  const handleZoomIn = () => {
    setTransform((prev) => {
      const newScale = clamp(prev.scale * 1.3, MIN_SCALE, MAX_SCALE);
      return zoomAtPoint(0, 0, newScale, prev);
    });
  };

  const handleZoomOut = () => {
    setTransform((prev) => {
      const newScale = clamp(prev.scale / 1.3, MIN_SCALE, MAX_SCALE);
      if (newScale <= MIN_SCALE) return { x: 0, y: 0, scale: MIN_SCALE };
      return zoomAtPoint(0, 0, newScale, prev);
    });
  };

  const handleReset = () => {
    setTransform({ x: 0, y: 0, scale: 1 });
  };

  // Cross-origin-friendly download: fetch the image as a blob and trigger a
  // programmatic save. Falls back to a plain anchor if fetch fails (e.g. CORS).
  const handleDownload = useCallback(async () => {
    const fallbackName =
      downloadFilename ||
      (() => {
        try {
          const u = new URL(imageUrl);
          const last = u.pathname.split("/").pop();
          return last || "image.jpg";
        } catch {
          return "image.jpg";
        }
      })();
    try {
      const res = await fetch(imageUrl, { mode: "cors", credentials: "omit" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      a.download = fallbackName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch {
      const a = document.createElement("a");
      a.href = imageUrl;
      a.download = fallbackName;
      a.target = "_blank";
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
    }
  }, [imageUrl, downloadFilename]);

  // Mouse drag for panning
  const handleMouseDown = (e: React.MouseEvent) => {
    if (transform.scale > 1) {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
      setTransformStart({ x: transform.x, y: transform.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && transform.scale > 1) {
      e.preventDefault();
      setTransform((prev) => ({
        ...prev,
        x: transformStart.x + (e.clientX - dragStart.x),
        y: transformStart.y + (e.clientY - dragStart.y),
      }));
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Double-click to toggle zoom
  const handleDoubleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (transform.scale > 1) {
      handleReset();
    } else {
      const cursor = getCursorRelativeToImageCenter(e.clientX, e.clientY);
      setTransform((prev) => zoomAtPoint(cursor.x, cursor.y, 2.5, prev));
    }
  };

  // Touch handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      touchStateRef.current = {
        distance: dist,
        scale: transform.scale,
        tx: transform.x,
        ty: transform.y,
      };
    } else if (e.touches.length === 1 && transform.scale > 1) {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
      setTransformStart({ x: transform.x, y: transform.y });
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && touchStateRef.current) {
      e.preventDefault();

      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );

      const midClientX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
      const midClientY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
      const cursor = getCursorRelativeToImageCenter(midClientX, midClientY);

      const newScale = clamp(
        (touchStateRef.current.scale / touchStateRef.current.distance) * dist,
        MIN_SCALE,
        MAX_SCALE
      );

      if (newScale <= MIN_SCALE) {
        setTransform({ x: 0, y: 0, scale: MIN_SCALE });
      } else {
        // Use the stored transform as base for pinch zoom
        const base: Transform = {
          x: touchStateRef.current.tx,
          y: touchStateRef.current.ty,
          scale: touchStateRef.current.scale,
        };
        setTransform(zoomAtPoint(cursor.x, cursor.y, newScale, base));
      }
    } else if (e.touches.length === 1 && isDragging && transform.scale > 1) {
      e.preventDefault();
      setTransform((prev) => ({
        ...prev,
        x: transformStart.x + (e.touches[0].clientX - dragStart.x),
        y: transformStart.y + (e.touches[0].clientY - dragStart.y),
      }));
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (e.touches.length < 2) {
      touchStateRef.current = null;
    }
    if (e.touches.length === 0) {
      setIsDragging(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black/95 z-[9999] flex items-center justify-center"
      onClick={onClose}
    >
      {/* Controls */}
      <div className="absolute top-6 right-6 z-10 flex items-center gap-3">
        <div className="bg-black/50 backdrop-blur-sm rounded-full px-4 py-2 flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleZoomOut();
            }}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
            title="Zoom Out"
          >
            <ZoomOut className="w-5 h-5 text-white" />
          </button>
          <span className="text-white text-sm font-medium min-w-[60px] text-center">
            {Math.round(transform.scale * 100)}%
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleZoomIn();
            }}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
            title="Zoom In"
          >
            <ZoomIn className="w-5 h-5 text-white" />
          </button>
          <div className="w-px h-6 bg-white/20 mx-1" />
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleReset();
            }}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
            title="Reset View"
          >
            <RotateCcw className="w-5 h-5 text-white" />
          </button>
          {allowDownload && (
            <>
              <div className="w-px h-6 bg-white/20 mx-1" />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload();
                }}
                className="p-2 hover:bg-white/10 rounded-full transition-colors"
                title="Download image"
              >
                <Download className="w-5 h-5 text-white" />
              </button>
            </>
          )}
        </div>
        <button
          onClick={onClose}
          className="p-3 bg-black/50 backdrop-blur-sm hover:bg-white/10 rounded-full transition-colors"
          title="Close (ESC)"
        >
          <X className="w-6 h-6 text-white" />
        </button>
      </div>

      {/* Image Container */}
      <div
        ref={containerRef}
        className="relative w-full h-full flex items-center justify-center overflow-hidden"
        style={{ touchAction: "none" }}
        onClick={(e) => e.stopPropagation()}
        onDoubleClick={handleDoubleClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <img
          ref={imgRef}
          src={imageUrl}
          alt={alt}
          className="max-w-[90vw] max-h-[90vh] select-none"
          style={{
            transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.scale})`,
            willChange: "transform",
            cursor:
              transform.scale > 1
                ? isDragging
                  ? "grabbing"
                  : "grab"
                : "zoom-in",
          }}
          draggable={false}
        />
      </div>

      {/* Instructions */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 bg-black/50 backdrop-blur-sm rounded-full px-6 py-3 text-white text-sm pointer-events-none">
        {transform.scale > 1
          ? "Drag to pan • Scroll to zoom • Double-click to reset"
          : "Scroll or pinch to zoom • Double-click to zoom in • Click outside to close"}
      </div>
    </div>
  );
}
